/**
 * Which info panel a tag opens in. AVA has three side-by-side slots (panelIds.js):
 * - a regular tag            → PANEL_IDS.TAGS                (forgeService.showEditTagUI)
 * - an inspection (AMIS) tag → PANEL_IDS.INSPECTION_INFO     (forgeService.showEditInspectionUI)
 * - an AVA Inspect tag       → PANEL_IDS.AVA_INSPECTION_INFO (forgeService.showEditAvaInspectUI)
 *
 * Each slot keeps its own editing state, so opening one never replaces the tag shown in another.
 * A card click decides by the tag's class (iconThumbnailPopup.vue editTag, nodeDetails.vue,
 * nodeControls.vue showInfo); this module is that rule in one place. A plain search row (not a
 * ForgeBaseTag) carries its kind in `type` instead, as nodeControls.vue reads it.
 */
import { ForgeAvaInspect, ForgeBaseTag, ForgeInspection } from '@/scripts/js/forgeModels';

export const TAG_INFO_PANEL_KIND = Object.freeze({
  TAG: 'tag',
  INSPECTION: 'inspection',
  AVA_INSPECT: 'ava-inspect',
});

/** A search row's `type` for the two inspection kinds. A model's own `type` is a category, never read. */
const KIND_BY_ROW_TYPE = Object.freeze({
  AvaInspect: TAG_INFO_PANEL_KIND.AVA_INSPECT,
  Inspection: TAG_INFO_PANEL_KIND.INSPECTION,
});

/** A search row whose `type` is a tag class name (ForgePipeline, ForgeAvaInspect, ...) is a tag row. */
const TAG_ROW_TYPE_PREFIX = 'Forge';

/**
 * True when `item` is a tag: a tag model (what the store holds for a global-search pick, a ForgePipeline with no
 * `type` of its own), or a plain row whose `type` names a tag class or one of the two inspection kinds. A document,
 * scan or any other row is not. The one rule for "is this a tag" (the assistant's page context reads it).
 */
export function isTagItem(item) {
  if (!item) return false;
  if (item instanceof ForgeBaseTag) return true;
  return typeof item.type === 'string'
    && (item.type.startsWith(TAG_ROW_TYPE_PREFIX) || Object.prototype.hasOwnProperty.call(KIND_BY_ROW_TYPE, item.type));
}

/** @returns {'tag'|'inspection'|'ava-inspect'} the info panel kind for `tag` */
export function tagInfoPanelKind(tag) {
  if (tag instanceof ForgeAvaInspect) return TAG_INFO_PANEL_KIND.AVA_INSPECT;
  if (tag instanceof ForgeInspection) return TAG_INFO_PANEL_KIND.INSPECTION;
  if (!tag || tag instanceof ForgeBaseTag) return TAG_INFO_PANEL_KIND.TAG;
  return Object.prototype.hasOwnProperty.call(KIND_BY_ROW_TYPE, tag.type)
    ? KIND_BY_ROW_TYPE[tag.type]
    : TAG_INFO_PANEL_KIND.TAG;
}

/**
 * Open `tag` with the opener for its kind.
 * @param {object} tag
 * @param {{ tag: Function, inspection: Function, avaInspect: Function }} openers one per slot
 */
export function openTagInfoPanel(tag, openers) {
  switch (tagInfoPanelKind(tag)) {
    case TAG_INFO_PANEL_KIND.AVA_INSPECT: return openers.avaInspect(tag);
    case TAG_INFO_PANEL_KIND.INSPECTION: return openers.inspection(tag);
    default: return openers.tag(tag);
  }
}
